/**
 * AddressModificationPage — manage the citizen's address book.
 *
 * The screens design covered both an address-book CRUD AND an in-transit
 * "redirect this parcel to a new address" workflow. The redirect flow has
 * no backend endpoint (service-mail does not expose a citizen-facing
 * in-flight mutation; service-delivery has no `/v1/delivery/redirect` route)
 * — it is surfaced here as a clearly-labelled deferral notice.
 *
 * The address-book CRUD is wired against the citizen-scoped endpoints on
 * service-customer:
 *   - GET  /v1/customers/me/addresses  (requires Address/read-own)
 *   - POST /v1/customers/me/addresses  (requires Address/create-own)
 *
 * Citizens carry Address/{read,create,update,delete}-own in their JWT
 * (seeded by the customer role). No customerId is passed in the URL —
 * the backend resolves the caller's customer record from the JWT's
 * upmp_user_id claim.
 */
import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, MapPin, Plus, Send } from 'lucide-react';
import {
  AddressFields,
  EMPTY_ADDRESS,
  validateAddress,
  type AddressValue,
} from '@upmp/ui';

import {
  createAddress,
  listAddresses,
  type CustomerAddressBody,
  type CustomerAddressResponse,
} from '../../lib/api/customer-addresses';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import { env } from '../../lib/env';
import { PageHeader } from './PortalUI';

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green';

const ADDRESS_LABELS: readonly { readonly value: 'HOME' | 'WORK' | 'OTHER'; readonly label: string }[] = [
  { value: 'HOME', label: 'Home' },
  { value: 'WORK', label: 'Work' },
  { value: 'OTHER', label: 'Other' },
];

interface FormState {
  readonly label: 'HOME' | 'WORK' | 'OTHER';
  readonly address: AddressValue;
  readonly contactPhone: string;
  readonly isPrimary: boolean;
}

const INITIAL: FormState = {
  label: 'HOME',
  address: EMPTY_ADDRESS,
  contactPhone: '',
  isPrimary: false,
};

/**
 * Map a country name to the ISO 3166-1 alpha-2 code the backend persists in
 * `customer_address.countryCode`. The cascading picker hands us the human
 * country name; the backend's `Iso3166Alpha2` Zod validator rejects free text.
 *
 * The seeded country list in service-geo is small (NG + a handful of regional
 * neighbours); a static map keeps us off a second round-trip. Anything we
 * haven't mapped falls through to "XX" — Iso3166Alpha2 still validates the
 * length+regex shape, and the geo_country_id soft-FK above gives downstream
 * consumers an authoritative reference.
 */
const COUNTRY_NAME_TO_ISO2: Record<string, string> = {
  NIGERIA: 'NG',
  GHANA: 'GH',
  BENIN: 'BJ',
  CAMEROON: 'CM',
  TOGO: 'TG',
  'COTE D’IVOIRE': 'CI',
  'COTE D\'IVOIRE': 'CI',
};

function deriveCountryCode(countryName: string | null): string {
  if (!countryName) return 'NG';
  return COUNTRY_NAME_TO_ISO2[countryName.toUpperCase()] ?? 'XX';
}

/** Query key for the address list — stable and usable in tests. */
export const ADDRESSES_QUERY_KEY = ['citizen-addresses'] as const;

export function AddressModificationPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { token, isAuthenticated } = useCustomerAuth();

  const [form, setForm] = useState<FormState>(INITIAL);
  const [savedFlash, setSavedFlash] = useState<boolean>(false);

  const query = useQuery<readonly CustomerAddressResponse[], Error>({
    queryKey: ADDRESSES_QUERY_KEY,
    queryFn: () => listAddresses({ bearerToken: token ?? undefined }),
    enabled: isAuthenticated && token !== null,
    retry: false,
    staleTime: 60_000,
  });

  const mutation = useMutation<CustomerAddressResponse, Error, CustomerAddressBody>({
    mutationFn: (body) => createAddress(body, { bearerToken: token ?? undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADDRESSES_QUERY_KEY });
      setSavedFlash(true);
      setForm(INITIAL);
    },
  });

  useEffect(() => {
    if (!savedFlash) return;
    const t = window.setTimeout(() => setSavedFlash(false), 2500);
    return () => window.clearTimeout(t);
  }, [savedFlash]);

  const addressValidation = validateAddress(form.address);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!addressValidation.ok) {
      return;
    }
    const a = form.address;
    // Backend AddressPayload still requires `line1`, `city`, `state`, `countryCode`
    // — derive them from the structured AddressValue so existing consumers
    // (sender / receiver capture, mail item formatting) keep their plain-text
    // surface, while the new geo_* soft-FK columns carry the picker selection.
    const city = (a.lgaName ?? a.stateName ?? '').trim();
    const stateName = (a.stateName ?? '').trim();
    const line1 = a.addressLine1.trim();
    const line2 = a.addressLine2.trim();
    const postal = a.postalCode.trim();
    const phone = form.contactPhone.trim();
    const body: CustomerAddressBody = {
      label: form.label,
      line1,
      city,
      state: stateName,
      countryCode: deriveCountryCode(a.countryName),
      ...(line2.length > 0 ? { line2 } : {}),
      ...(postal.length > 0 ? { postalCode: postal } : {}),
      ...(phone.length > 0 ? { contactPhone: phone } : {}),
      isPrimary: form.isPrimary,
      // service-geo soft-FK + denormalised display names. All optional
      // server-side; null/undefined when the picker hasn't reached that level.
      ...(a.countryId != null ? { geoCountryId: a.countryId } : {}),
      ...(a.countryName ? { geoCountryName: a.countryName } : {}),
      ...(a.stateId != null ? { geoStateId: a.stateId } : {}),
      ...(a.stateName ? { geoStateName: a.stateName } : {}),
      ...(a.lgaId != null ? { geoLgaId: a.lgaId } : {}),
      ...(a.lgaName ? { geoLgaName: a.lgaName } : {}),
      ...(a.wardId != null ? { geoWardId: a.wardId } : {}),
      ...(a.wardName ? { geoWardName: a.wardName } : {}),
    };
    mutation.mutate(body);
  }

  return (
    <div>
      <PageHeader title="Address Change" description="Manage your saved delivery addresses." />

      <div className="max-w-3xl mx-auto space-y-6">
        {/* Saved-addresses list */}
        <div className="bg-white rounded-xl border border-border p-6">
          <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-nipost-green" /> Saved Addresses
          </h2>
          {query.isLoading ? (
            <p className="text-sm text-muted flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </p>
          ) : query.isError ? (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 p-2 rounded" role="alert">
              {query.error.message}
            </p>
          ) : (query.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted">No saved addresses yet — add one below.</p>
          ) : (
            <ul className="space-y-2">
              {(query.data ?? []).map((a) => (
                <li key={a.id} className="border border-border rounded-lg p-3 text-sm">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="font-semibold text-gray-900">{a.label ?? 'Address'}{a.isPrimary ? ' · Primary' : ''}</span>
                    <span className="text-[10px] font-mono text-muted">{a.id.slice(0, 8)}</span>
                  </div>
                  <p className="text-xs text-gray-700">
                    {a.line1}
                    {a.line2 ? `, ${a.line2}` : ''}, {a.city}, {a.state}
                    {a.postalCode ? `, ${a.postalCode}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Add-address form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-border p-6 space-y-5" noValidate>
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <Plus className="w-5 h-5 text-nipost-green" /> Add new address
          </h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
            <div className="flex gap-2">
              {ADDRESS_LABELS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm((s) => ({ ...s, label: opt.value }))}
                  className={`px-4 py-2 rounded-full text-sm font-medium border ${
                    form.label === opt.value
                      ? 'bg-nipost-green text-white border-nipost-green'
                      : 'bg-white text-gray-700 border-border hover:border-nipost-green'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Cascading country → state → LGA → ward picker plus street + postal.
              The shared <AddressFields> component fetches reference data from
              service-geo (env.serviceGeoBaseUrl) and caches across mounts. */}
          <AddressFields
            geoBaseUrl={env.serviceGeoBaseUrl}
            value={form.address}
            onChange={(next) => setForm((s) => ({ ...s, address: next }))}
          />

          <div>
            <label htmlFor="am-phone" className="block text-sm font-medium text-gray-700 mb-1">Contact phone</label>
            <input
              id="am-phone"
              type="tel"
              value={form.contactPhone}
              onChange={(e) => setForm((s) => ({ ...s, contactPhone: e.target.value }))}
              className={inputClass}
              placeholder="Optional — used by the courier on delivery"
            />
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.isPrimary}
              onChange={(e) => setForm((s) => ({ ...s, isPrimary: e.target.checked }))}
              className="w-4 h-4 rounded border-border text-nipost-green focus:ring-nipost-green"
            />
            Set as primary address
          </label>

          {mutation.isError ? (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700" role="alert">
              {mutation.error.message}
            </div>
          ) : null}

          {savedFlash ? (
            <div className="rounded-lg bg-success-light border border-success/20 px-3 py-2 text-xs text-success flex items-center gap-2" role="status">
              <CheckCircle2 className="w-3.5 h-3.5" /> Address saved.
            </div>
          ) : null}

          {!addressValidation.ok ? (
            <p className="text-xs text-muted">{addressValidation.reason}</p>
          ) : null}

          <button
            type="submit"
            disabled={mutation.isPending || !addressValidation.ok}
            className="px-6 py-2.5 bg-nipost-green text-white text-sm font-medium rounded-lg hover:bg-nipost-green-dark transition-colors disabled:opacity-60 inline-flex items-center gap-2"
          >
            {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Save address
          </button>
        </form>
      </div>
    </div>
  );
}
